import { useParams, Link, useNavigate } from "react-router-dom";
import { Eye, LogOut, Lock, Maximize2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, fmtClock, download, errMsg } from "../lib/api";
import { usePoll } from "../lib/usePoll";
import { useAuth } from "../lib/auth";
import { ParishMark, StatusBadge, Loading, PageHeader } from "../components/common";
import { SummaryCards, MeetingCharts, AttendanceTable, UpdatedAt } from "../components/LiveView";
import { MeetingDocuments } from "../components/MeetingDocuments";
import { SheetButtons, TrendBadge, Spark } from "../components/GroupTrends";

function MyGroups() {
  const { data } = usePoll(() => api.get("/leader/groups").then((r) => r.data), 60000);
  if (!data || !data.groups.length) return null;
  return (
    <section className="mb-8" data-testid="my-groups">
      <h2 className="font-serif text-xl font-bold text-[#2D3B2D] mb-1">My groups</h2>
      <p className="text-sm text-stone-500 mb-3">Last 6 months · {data.meetings} closed meeting(s). Member lists show names only, for follow-ups and invitations.</p>
      <div className="grid gap-4 md:grid-cols-2">
        {data.groups.map((g) => (
          <div key={`${g.type}-${g.id}`} className={`sfap-card p-5 grid gap-3 ${g.needs_encouragement ? "border-amber-300" : ""}`} data-testid={`my-group-${g.id}`}>
            <div className="flex justify-between gap-2"><div><div className="text-[11px] uppercase tracking-wider text-stone-500">{g.type === "gkk" ? "GKK" : "Ministry"}</div><div className="font-serif font-bold text-lg">{g.name}</div></div><TrendBadge trend={g.trend} /></div>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div><div className="text-2xl font-black">{g.members}</div>members</div>
              <div><div className="text-2xl font-black">{g.participation_rate}%</div>took part</div>
              <div><div className="text-2xl font-black">{g.inactive_members}</div>not seen yet</div>
            </div>
            <Spark monthly={g.monthly} />
            {g.needs_encouragement && <p className="text-xs font-semibold text-amber-800">Participation is low or falling. A personal invitation may help.</p>}
            <SheetButtons type={g.type} id={g.id} testid={`my-group-sheet-${g.id}`} />
          </div>
        ))}
      </div>
    </section>
  );
}

const Banner = () => (
  <div className="bg-[#78350F] text-[#FEF3C7] px-4 py-3 text-center font-bold tracking-wider text-sm flex items-center justify-center gap-2 sticky top-0 z-30" data-testid="readonly-banner">
    <Lock className="h-4 w-4" /> READ-ONLY MONITORING — NO EDITING PERMITTED
  </div>
);

function MeetingList() {
  const { data } = usePoll(() => api.get("/monitor/meetings").then((r) => r.data), 30000);
  if (!data) return <Loading />;
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {data.map((m) => (
        <Link key={`${m.id}-${m.assignment_id}`} to={`/monitor/${m.id}`} className="sfap-card p-5 hover:border-[#2D3B2D]" data-testid={`monitor-meeting-${m.code}`}>
          <div className="flex justify-between"><span className="font-mono text-xs text-stone-500">{m.code}</span><StatusBadge status={m.status} /></div>
          <div className="font-serif font-bold text-lg mt-1">{m.title}</div>
          <div className="text-sm text-stone-600">{fmtDate(m.date)} · {fmtClock(m.start_time)} · {m.venue}</div>
          <div className="text-xs mt-2 text-[#5C3A21] font-semibold">Scope: {m.scope_label} · authorization expires {new Date(m.expires_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}</div>
        </Link>
      ))}
      {!data.length && <p className="text-stone-500">No active monitoring authorization. Please contact the Parish Administrator.</p>}
    </div>
  );
}

function MonitorMeeting({ mid }) {
  const { data, error } = usePoll(() => api.get(`/monitor/meetings/${mid}/live`).then((r) => r.data), 5000, [mid]);
  if (error && !data) return <p className="text-red-700" data-testid="monitor-error">{errMsg(error)}</p>;
  if (!data) return <Loading />;
  const { meeting: m, stats } = data;
  const pdf = () => download(`/reports/meetings/${mid}/attendance.pdf`, `${m.code}-attendance.pdf`).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="grid gap-6">
      <PageHeader title={m.title} sub={`${m.code} · ${fmtDate(m.date)} · ${fmtClock(m.start_time)} · ${m.venue} · Scope: ${data.scope_label}`}>
        <StatusBadge status={m.status} testid="monitor-meeting-status" />
        <Link to={`/live/${mid}`} className="btn-sm btn-ghost" data-testid="monitor-projector-btn"><Maximize2 className="h-4 w-4" />Projector view</Link>
        <button className="btn-sm btn-ghost" onClick={pdf} data-testid="monitor-pdf-btn"><FileDown className="h-4 w-4" />Attendance PDF</button>
      </PageHeader>
      <UpdatedAt at={data.updated_at} />
      <SummaryCards s={stats} />
      <MeetingCharts s={stats} />
      <AttendanceTable rows={data.rows} showContacts={data.rows.some((r) => r.mobile || r.email)} testid="monitor-attendance-table" />
      <h2 className="font-serif text-xl font-bold text-[#2D3B2D]">Meeting documents</h2>
      <MeetingDocuments mid={mid} code={m.code} canEdit={false} />
    </div>
  );
}

export default function MonitorPortal() {
  const { mid } = useParams();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Banner />
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-8 py-4 border-b border-[#E5DEC9]">
        <Link to="/monitor"><ParishMark small /></Link>
        <div className="flex items-center gap-3 text-sm">
          <Eye className="h-4 w-4 text-[#5C3A21]" /><span className="font-semibold">{user.name}</span><span className="text-stone-500 hidden sm:inline">Authorized Attendance Monitor</span>
          <button onClick={() => { logout(); nav("/login"); }} className="btn-sm btn-ghost" data-testid="monitor-logout"><LogOut className="h-4 w-4" /></button>
        </div>
      </header>
      <main className="px-4 sm:px-8 py-8 max-w-[1500px] mx-auto">
        {mid ? <><Link to="/monitor" className="text-sm text-stone-500">← All authorized meetings</Link><div className="mt-3"><MonitorMeeting mid={mid} /></div></> : <><PageHeader title="Authorized Monitoring" sub="Meetings you are authorized to monitor. Information shown is limited to your assigned scope." /><MyGroups /><MeetingList /></>}
      </main>
    </div>
  );
}
